/**
 * Admin portal API auth: adds the access token to /v1 and /api calls, renews it with the
 * refresh token when the API answers 401, and sends the user back to the sign-in page
 * (with a message) when that fails.
 *
 * Before, the token was fixed at sign-in and nothing reacted to 401: after the 15-minute
 * access token expired, every click (Check for updates, Install …) silently did nothing.
 */
export const TOKEN_KEY = 'eurion_token';
export const REFRESH_KEY = 'eurion_refresh_token';
export const EXPIRED_FLAG = 'eurion_admin_session_expired';

// Calls that must not trigger a refresh (they are how you get a token in the first place)
const NO_REFRESH = /^\/(?:api\/)?v1\/auth\/(?:login|refresh|sso|azure|google|saml)/;

let installed = false;
let refreshing: Promise<string | null> | null = null;

function urlOf(input: RequestInfo | URL): string {
  return typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
}

export function saveTokens(accessToken: string, refreshToken?: string | null): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/** One refresh at a time; every request that got a 401 meanwhile waits for it. */
function refreshOnce(orig: typeof fetch): Promise<string | null> {
  if (!refreshing) {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    refreshing = (async () => {
      if (!refreshToken) return null;
      try {
        const res = await orig('/v1/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        const data = (await res.json()) as { success?: boolean; data?: { accessToken?: string; refreshToken?: string; tokens?: { accessToken?: string; refreshToken?: string } } };
        const t = data.data?.tokens ?? data.data;
        if (!res.ok || !data.success || !t?.accessToken) return null;
        saveTokens(t.accessToken, t.refreshToken);
        return t.accessToken;
      } catch {
        return null;
      }
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
}

function sessionExpired(): void {
  clearTokens();
  try { sessionStorage.setItem(EXPIRED_FLAG, '1'); } catch { /* private mode */ }
  window.location.reload();
}

/** Installs the fetch wrapper once; the token is read from localStorage on every call. */
export function installAuthInterceptor(): void {
  if (installed) return;
  installed = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = urlOf(input);
    if (!url.startsWith('/v1/') && !url.startsWith('/api/')) return orig(input, init);
    const withToken = (token: string | null): RequestInit => {
      const headers = new Headers((init as RequestInit).headers ?? {});
      if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
      return { ...init, headers };
    };
    const res = await orig(input, withToken(localStorage.getItem(TOKEN_KEY)));
    if (res.status !== 401 || NO_REFRESH.test(url) || !localStorage.getItem(TOKEN_KEY)) return res;
    const fresh = await refreshOnce(orig);
    if (!fresh) { sessionExpired(); return res; }
    return orig(input, withToken(fresh));
  };
}

/** True once after a forced sign-out, so the sign-in page can say why. */
export function takeExpiredFlag(): boolean {
  try {
    const v = sessionStorage.getItem(EXPIRED_FLAG) === '1';
    sessionStorage.removeItem(EXPIRED_FLAG);
    return v;
  } catch {
    return false;
  }
}

/** For tests only. */
export function resetAuthInterceptorForTests(): void {
  installed = false;
  refreshing = null;
}
